import _ from "lodash";
import React, { useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import { graphql, compose } from "lib/apolloClient";

import {
  ChecklistDropdown,
  FilterLabelButton,
  Dropdown,
  DropdownMenu,
  Button,
  IconButton,
  Tooltip,
  Badge,
  IconDisclosure,
  TextInput,
} from "@toddle-edu/ds-web";
import {
  AddOutlined,
  BadgeTickFilled,
  RefreshArrowOutlined,
  ShareOutlined,
  MinusCircleOutlined,
  PencilLineOutlined,
  AddCircleOutlined,
  AttachmentClipOutlined,
  HeadHeartOutlined,
  FilterOutlined,
} from "@toddle-edu/ds-icons";
import { elevations } from "@toddle-edu/ds-theme";

import withLoader from "UIComponents/GqlWithLoader";
import FullScreenLoader from "UIComponents/FullScreenLoader";

import classes from "./GPTConversation.scss";
import { useI18n, useTextToSpeech } from "Hooks";
import SystemPromptModal from "../SystemPromptModal";
import { copyToClipboard } from "Utils/CopyToClipboard";
import { setToastMsg } from "routes/modules/LoginModule";

import {
  getLabelValueForMultiFilterLabelButton,
  checkScrollAtBottom,
  scrollIntoView,
} from "Utils";
import { getAIConversationsQueryWithPublicUrlFromCache } from "modules/CommonGraphqlHelpers";
import { getAIConversationsQueryWithPublicUrl } from "modules/CommonQuery";

import { getUserId } from "modules/CommonSelectors";

import {
  onSendQuery,
  cancelRequest,
  createNewAiMessageInCache,
  createNewAIMessageMutationWithTemporaryId,
  createAIConversationAttachments,
  deleteAIConversationAttachment,
  onGenerateImageQuery,
  updateAIMessage,
} from "../../modules/GPTPlaygroundModule";
import {
  getToolOptions,
  getModelOptions,
  TOOLS_ENABLED_MODELS,
  IMAGE_GENERATION_MODELS,
  getAspectRationOptions,
  getImageTypeOptions,
  getMoreFilterOptions,
  REASONING_EFFORT_TOKENS_MAP,
  processMessageContent,
} from "../../modules/Utils";
import { getIsAIResponseLoading } from "ClassRoom/components/AddAssignment/LessonPlanningAssistant/modules/Utils";
import { resetAIConversation, updateAIConversation } from "modules/Services";
import MessageComponent from "../MessageComponent";
import AttachmentsModal from "../AttachmentsModal";
import MemoriesModal from "../MemoriesModal";

const styles = {
  authorTypeButtonStyles: { width: "108px" },
};

const getMessageActionButtons = props => {
  const disabled = _.get(props, "disabled", false);
  return (
    <div className={classes.messageAction}>
      <IconButton
        size={"small"}
        variant={"outlined-subtle"}
        icon={<PencilLineOutlined />}
        disabled={disabled}
      />
      <IconButton
        size={"small"}
        variant={"outlined-subtle"}
        icon={<MinusCircleOutlined />}
        disabled={disabled}
      />
    </div>
  );
};

const GPTConversation = props => {
  const {
    messages,
    onSendQuery,
    setIsLoading,
    aiConversationId,
    userId,
    resetAIConversation,
    updateAIConversation,
    createNewAiMessageInCache,
    createNewAIMessageMutationWithTemporaryId,
    metadata,
    setToastMsg,
    isPublicView,
    publicUrl,
    setPublicViewTitle,
    editMessageId,
    setEditMessageId,
    conversationAttachments,
    onGenerateImageQuery,
    updateAIMessage,
    agentPrompts,
  } = props;

  const [conversationDetailsObj, setConversationDetailsObj] = useState({
    systemPrompt: metadata.systemPrompt,
    model: metadata.model,
    tools: metadata.tools,
    toolChoice: "auto",
    aspectRatio: "16:9",
    imageType: "jpeg",
    reasoningEffort: "NONE",
    temperature: 0.7,
  });
  const [isSystemPromptModalOpen, setIsSystemPromptModalOpen] = useState(false);
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const [isAttachmentsListOpen, setIsAttachmentsListOpen] = useState(false);
  const [currentAIState, setCurrentAIState] = useState("");
  const [shouldScrollToBottom, setShouldScrollToBottom] = useState(true);
  const [toolName, setToolName] = useState("");
  const [isMemoriesModalOpen, setMemoriesModalOpen] = useState(false);

  const onAudioURLReceivedForMessage = async ({
    generatedAudioURL,
    itemId,
  }) => {
    if (!_.isEmpty(generatedAudioURL)) {
      const messageNode = _.find(messages, { node: { id: itemId } });
      if (!_.isEmpty(messageNode))
        await updateAIMessage({
          aiConversationId,
          variables: {
            id: itemId,
            metadata: {
              ..._.get(messageNode, "node.metadata", {}),
              audioUrl: generatedAudioURL,
            },
          },
        });
    }
  };
  const {
    handleSpeakClick,
    isAudioGenerating,
    currentlyAudioPlayingForItemId,
  } = useTextToSpeech({
    onAudioURLReceived: onAudioURLReceivedForMessage,
  });

  const scrollEndRef = useRef(null);

  const { t } = useI18n(["classRoom", "common"]);
  const modelOptions = getModelOptions();

  const {
    systemPrompt,
    model,
    tools,
    toolChoice,
    aspectRatio,
    imageType,
    temperature,
  } = conversationDetailsObj;
  // functions
  const scrollToBottom = () => {
    if (scrollEndRef.current !== null && shouldScrollToBottom) {
      scrollIntoView({
        element: scrollEndRef.current,
        checkForElementInViewPort: true,
      });
    }
  };

  // on message sent scroll to bottom
  useEffect(() => {
    if (!_.isEmpty(messages)) {
      scrollToBottom();
    }
  }, [messages, shouldScrollToBottom, currentAIState, conversationAttachments]);

  useEffect(() => {
    const isModelDeprecated = _.isNil(
      _.find(modelOptions, modelConfig => metadata.model === modelConfig.key)
    );
    setConversationDetailsObj({
      systemPrompt: metadata.systemPrompt,
      model: !isModelDeprecated ? metadata.model : "openai:gpt-4o",
      tools: metadata.tools,
      toolChoice: "auto",
      aspectRatio: "16:9",
      imageType: "jpeg",
      temperature: metadata.temperature,
    });

    if (isModelDeprecated) {
      updateConversation({ valueObj: { model: "openai:gpt-4o" } });
    }

    setToolName("");
    setCurrentAIState("");

    if (isPublicView) {
      setPublicViewTitle(_.get(metadata, "label", "Playground"));
    }
  }, [metadata]);

  //Handles cases where old conversations have old model keys
  useEffect(() => {
    const isNewModelKey = _.size(_.split(model, ":")) > 1;
    if (!isNewModelKey) {
      setConversationDetailsObj({
        ...conversationDetailsObj,
        model: `openai:${model}`,
      });
    }
  }, [conversationDetailsObj]);

  //When we have old search or m_click tool, we need to convert it to new internet_browsing tool
  useEffect(() => {
    if (
      !isPublicView &&
      !_.isEmpty(_.intersection(tools, ["search", "m_click"]))
    ) {
      updateConversationWithNewSearchKey({ tools });
    }
  }, [isPublicView, tools]);

  const updateConversationWithNewSearchKey = async ({ tools }) => {
    await updateConversation({
      valueObj: {
        tools: [..._.without(tools, "search", "m_click"), "internet_browsing"],
      },
    });
  };

  const onScrollBodyContainer = e => {
    setShouldScrollToBottom(checkScrollAtBottom(e.target, 250));
  };

  const onSubmitMessages = async () => {
    setShouldScrollToBottom(true);
    const combinedMessages = systemPrompt
      ? [
          {
            role: "system",
            content: systemPrompt,
          },
        ]
      : [];

    _.forEach(messages, ({ node }) => {
      const { content, authorType } = node || {};
      let metadata = _.get(node, "metadata", {});

      const imageAttachments = _.filter(
        _.get(node, "attachments", []),
        attachment => attachment.type === "IMAGE"
      );
      if (imageAttachments?.length > 0) {
        metadata = {
          ...metadata,
          imageUrls: _.map(
            imageAttachments,
            attachment =>
              _.get(attachment, "signedUrl") || _.get(attachment, "url")
          ),
        };
      }

      const processedContent =
        authorType === "user" ? processMessageContent(content) : content;
      combinedMessages.push({
        role: authorType,
        content: processedContent,
        metadata,
      });
    });

    const finalTools = _.includes(tools, "internet_browsing")
      ? [..._.without(tools, "internet_browsing"), "search", "m_click"]
      : tools;

    const toolChoiceObj = !_.isEmpty(tools)
      ? {
          toolChoice: _.includes(["auto", "none"], toolChoice)
            ? toolChoice
            : {
                type: "function",
                function: {
                  name: _.isEqual(toolChoice, "internet_browsing")
                    ? "search"
                    : toolChoice,
                },
              },
        }
      : {};

    try {
      switch (model) {
        case "stability:stable-image-core":
          {
            await onGenerateImageQuery({
              t,
              aiConversationId,
              setCurrentAIState,
              input: {
                entityId: userId,
                entityType: "USER",
                model: _.last(_.split(model, ":")),
                combinedMessages,
                imageFormat: imageType,
                aspectRatio,
              },
            });
          }
          break;
        default:
          await onSendQuery({
            t,
            aiConversationId,
            setCurrentAIState,
            input: {
              entityId: userId,
              entityType: "USER",
              model,
              toolNames: finalTools,
              messages: combinedMessages,
              ...toolChoiceObj,
              stream: true,
              temperature: parseFloat(
                _.get(conversationDetailsObj, "temperature", 0.7)
              ),
              metadata: {
                conversationId: aiConversationId,
                knowledgeBase: {
                  entities: [
                    {
                      entityType: "AI_CONVERSATION",
                      entityIds: [aiConversationId],
                    },
                  ],
                },
              },
              maxTokens: _.includes(
                ["bedrock:claude-3.5-sonnet-v2", "bedrock:claude-3.5-sonnet"],
                model
              )
                ? 8192
                : model === "bedrock:claude-3.7-sonnet"
                ? 40000
                : undefined,
              shouldProcessAttachments: true,
              reasoningEnabled:
                conversationDetailsObj.reasoningEffort &&
                conversationDetailsObj.reasoningEffort !== "NONE",
              reasoningTokens: _.get(
                REASONING_EFFORT_TOKENS_MAP,
                conversationDetailsObj.reasoningEffort,
                0
              ),
            },
            setToolName,
          });
      }
      setToolName("");
    } catch (e) {
      console.error(e);
    }
  };

  const onSystemPromptButtonClick = () => {
    setIsSystemPromptModalOpen(true);
  };

  const onSystemPromptModalClose = () => {
    setIsSystemPromptModalOpen(false);
  };

  const toggleMoreFilters = () => setShowMoreFilters(prev => !prev);

  const onSystemPromptUpdate = prompt => {
    setConversationDetailsObj({
      ...conversationDetailsObj,
      systemPrompt: prompt,
    });
    updateConversation({ valueObj: { systemPrompt: prompt } });
  };

  const onToggleMemoriesModal = () => {
    setMemoriesModalOpen(!isMemoriesModalOpen);
  };

  const resetConversation = async () => {
    setCurrentAIState("LOADING");
    await resetAIConversation({
      input: {
        id: aiConversationId,
      },
    });
    setCurrentAIState("");
  };

  const updateConversation = async ({ valueObj }) => {
    await updateAIConversation({
      input: {
        id: aiConversationId,
        metadata: {
          ...metadata,
          ...valueObj,
        },
      },
    });
  };

  const onClickStop = () => {
    cancelRequest?.();
    setCurrentAIState("");
  };

  const toolOptions = getToolOptions();
  _.forEach(agentPrompts, agentPrompt => {
    toolOptions.push({
      value: `agent:${_.get(agentPrompt, "type")}`,
      label: _.get(agentPrompt, "displayText"),
    });
  });
  const groupedToolOptions = [
    {
      label: "Core Tools",
      value: "core_tools",
      options: _.filter(
        toolOptions,
        tool => !_.startsWith(tool.value, "agent:")
      ),
    },
    {
      label: "Agent Tools",
      value: "agent_tools",
      options: _.filter(toolOptions, tool =>
        _.startsWith(tool.value, "agent:")
      ),
    },
  ];

  const aspectRatioOptions = getAspectRationOptions();
  const imageTypeOptions = getImageTypeOptions();
  const moreFilterOptions = getMoreFilterOptions({
    selectedModel: conversationDetailsObj.model,
  });
  const toolCallOptions = [
    {
      key: "auto",
      label: "Auto",
    },
    {
      key: "none",
      label: "None",
    },
  ];

  _.forEach(tools, tool => {
    const toolOption = _.find(toolOptions, { value: tool });
    if (toolOption) {
      toolCallOptions.push({
        key: tool,
        label: toolOption.label,
      });
    }
  });

  const handleShareLinkCopy = () => {
    copyToClipboard(publicUrl, () => {
      setToastMsg({
        msg: "toastMsgs:copied_capital",
        type: "success",
      });
    });
  };

  const createEmptyMessage = async () => {
    const lastMessage = _.last(messages);
    const lastMessageAuthorType = _.get(
      lastMessage,
      "node.authorType",
      "assistant"
    );

    const cacheResult = await createNewAiMessageInCache({
      aiConversationId,
      authorType: lastMessageAuthorType === "assistant" ? "user" : "assistant",
      query: "",
      attachments: [],
    });

    const { generatedId } = cacheResult;

    if (generatedId) {
      setEditMessageId(generatedId);
      const { realMessageId } = await createNewAIMessageMutationWithTemporaryId(
        {
          aiConversationId,
          authorType:
            lastMessageAuthorType === "assistant" ? "user" : "assistant",
          content: "",
          ...cacheResult,
        }
      );
      setEditMessageId(realMessageId);
    }
  };

  const isSystemPromptFilled = !_.isEmpty(systemPrompt);
  const modelLabelValue = _.find(modelOptions, {
    key: model,
  })?.label;
  const toolChoiceLabel = _.find(toolCallOptions, { key: toolChoice })?.label;

  const renderMessages = () => {
    return (
      <React.Fragment>
        {_.map(messages, ({ node }, nodeIndex) => {
          return (
            <MessageComponent
              key={"AI_MESSAGE_CONTENT_" + node.id}
              messages={messages}
              messagesLength={_.size(messages)}
              aiConversationId={aiConversationId}
              node={node}
              nodeIndex={nodeIndex}
              isPublicView={isPublicView}
              setIsLoading={setIsLoading}
              editMessageId={editMessageId}
              setEditMessageId={setEditMessageId}
              isMessageAudioGenerating={isAudioGenerating}
              messageAudioPlayingForId={currentlyAudioPlayingForItemId}
              handleSpeakClick={handleSpeakClick}
            />
          );
        })}

        {currentAIState === "WRITING" ? (
          <div className={classes.assistantMsg}>
            <div className={classes.generatingContent}>
              <Button
                size={"small"}
                variant={"outlined-subtle"}
                isFullWidth={true}
                style={styles.authorTypeButtonStyles}
              >
                {"ASSISTANT"}
              </Button>

              <div className={classes.messageUnitContainer}>
                <div className={classes.generatingMsgContainer}>
                  <div className={classes.generatingMsg}>
                    <div className={classes.generatingSingleMsg}>
                      {toolName || t("classRoom:writing")}
                    </div>
                    <div className={classes.dot} />
                  </div>
                </div>
                {getMessageActionButtons({ disabled: true })}
              </div>
            </div>
          </div>
        ) : null}

        {_.isEmpty(currentAIState) && !isPublicView ? (
          <div className={classes.addNewMessageButtonContainer}>
            <Button
              size={"small"}
              variant={"outlined"}
              icon={<AddCircleOutlined />}
              onClick={createEmptyMessage}
            >
              {"Add Message"}
            </Button>
          </div>
        ) : null}
      </React.Fragment>
    );
  };
  return (
    <>
      <div className={classes.container}>
        <div className={classes.mergedHeaderContainer}>
          <div className={classes.headerContainer}>
            <div className={classes.leftHeaderContainer}>
              <Dropdown
                overlay={
                  <DropdownMenu
                    options={modelOptions}
                    onClick={({ key }) => {
                      setConversationDetailsObj({
                        ...conversationDetailsObj,
                        model: key,
                      });
                      updateConversation({ valueObj: { model: key } });
                    }}
                    selectionType="radio"
                    value={model}
                  />
                }
                disabled={isPublicView}
              >
                <FilterLabelButton
                  label={"Model"}
                  labelValue={modelLabelValue}
                  variant="outlined-subtle"
                />
              </Dropdown>
              {!_.includes(IMAGE_GENERATION_MODELS, model) ? (
                <ChecklistDropdown
                  placement={"bottomRight"}
                  onChange={selectedOptions => {
                    setConversationDetailsObj({
                      ...conversationDetailsObj,
                      tools: selectedOptions,
                    });
                    updateConversation({
                      valueObj: { tools: selectedOptions },
                    });
                  }}
                  isSearchEnabled={false}
                  options={groupedToolOptions}
                  value={tools}
                  disabled={
                    isPublicView || !_.includes(TOOLS_ENABLED_MODELS, model)
                  }
                >
                  <FilterLabelButton
                    label={"Tools"}
                    labelValue={getLabelValueForMultiFilterLabelButton({
                      options: toolOptions,
                      selectedOptions: tools,
                      t,
                      noneText: t("common:select"),
                      allText: t("common:all"),
                    })}
                    variant="outlined-subtle"
                  />
                </ChecklistDropdown>
              ) : null}
              {!_.includes(IMAGE_GENERATION_MODELS, model) &&
                !_.isEmpty(tools) && (
                  <Dropdown
                    overlay={
                      <DropdownMenu
                        options={toolCallOptions}
                        onClick={({ key }) =>
                          setConversationDetailsObj({
                            ...conversationDetailsObj,
                            toolChoice: key,
                          })
                        }
                        selectionType="radio"
                        value={model}
                      />
                    }
                    disabled={
                      isPublicView || !_.includes(TOOLS_ENABLED_MODELS, model)
                    }
                  >
                    <FilterLabelButton
                      label={"Tool call"}
                      labelValue={toolChoiceLabel}
                      variant="outlined-subtle"
                    />
                  </Dropdown>
                )}
              {_.includes(IMAGE_GENERATION_MODELS, model) ? (
                <Dropdown
                  overlay={
                    <DropdownMenu
                      options={aspectRatioOptions}
                      onClick={({ key }) => {
                        setConversationDetailsObj({
                          ...conversationDetailsObj,
                          aspectRatio: key,
                        });
                      }}
                      selectionType="radio"
                      value={aspectRatio}
                    />
                  }
                  disabled={isPublicView}
                >
                  <FilterLabelButton
                    label={"Aspect ratio"}
                    labelValue={aspectRatio}
                    variant="outlined-subtle"
                  />
                </Dropdown>
              ) : null}
              {_.includes(IMAGE_GENERATION_MODELS, model) ? (
                <Dropdown
                  overlay={
                    <DropdownMenu
                      options={imageTypeOptions}
                      onClick={({ key }) => {
                        setConversationDetailsObj({
                          ...conversationDetailsObj,
                          imageType: key,
                        });
                      }}
                      selectionType="radio"
                      value={imageType}
                    />
                  }
                  disabled={isPublicView}
                >
                  <FilterLabelButton
                    label={"Image type"}
                    labelValue={imageType}
                    variant="outlined-subtle"
                  />
                </Dropdown>
              ) : null}
              <IconDisclosure
                dsVersion={"2.0"}
                variant={"neutral"}
                type={"outlined"}
                size={"medium"}
                icon={<FilterOutlined />}
                onClick={toggleMoreFilters}
                isActivated={showMoreFilters}
                disabled={isPublicView || _.isEmpty(moreFilterOptions)}
              />
            </div>
            <div className={classes.leftHeaderContainer}>
              <Button
                icon={
                  isSystemPromptFilled ? (
                    <BadgeTickFilled variant="success" />
                  ) : (
                    <AddOutlined />
                  )
                }
                variant="outlined-subtle"
                onClick={onSystemPromptButtonClick}
              >
                System prompt
              </Button>
              <Tooltip
                placement="top"
                tooltip={"Temperature (0-1): Controls randomness"}
              >
                <TextInput
                  value={temperature}
                  onChange={e => {
                    const value = parseFloat(e.target.value);
                    if (!_.isNaN(value) && value >= 0 && value <= 1) {
                      setConversationDetailsObj({
                        ...conversationDetailsObj,
                        temperature: value,
                      });
                      updateConversation({
                        valueObj: { temperature: value },
                      });
                    }
                  }}
                  placeholder="0-1"
                  type="number"
                  min="0"
                  max="1"
                  step="0.1"
                  disabled={isPublicView}
                  size="medium"
                  className={classes.temperatureInput}
                  variant="outlined"
                  dsVersion="2.0"
                />
              </Tooltip>
              <Badge
                type={"numeric"}
                value={_.size(conversationAttachments)}
                variant="filter"
              >
                <Tooltip placement="top" tooltip={"Manage attachment(s)"}>
                  <IconButton
                    icon={<AttachmentClipOutlined />}
                    variant={"outlined-subtle"}
                    onClick={() => setIsAttachmentsListOpen(true)}
                  />
                </Tooltip>
              </Badge>
              <Tooltip placement="top" tooltip={"Manage memories"}>
                <IconButton
                  variant={"outlined-subtle"}
                  icon={<HeadHeartOutlined />}
                  onClick={onToggleMemoriesModal}
                />
              </Tooltip>
              <Tooltip placement="top" tooltip={"Copy sharable link"}>
                <IconButton
                  variant={"outlined-subtle"}
                  icon={<ShareOutlined />}
                  onClick={handleShareLinkCopy}
                />
              </Tooltip>
              <Tooltip placement="top" tooltip={"Reset conversation"}>
                <IconButton
                  variant={"outlined-subtle"}
                  disabled={
                    getIsAIResponseLoading(currentAIState) ||
                    _.isEmpty(messages) ||
                    isPublicView
                  }
                  icon={<RefreshArrowOutlined />}
                  onClick={resetConversation}
                />
              </Tooltip>
            </div>
          </div>
          {showMoreFilters ? (
            <div className={classes.moreFiltersContainer}>
              {_.map(moreFilterOptions, filterOption => {
                switch (filterOption.type) {
                  case "SINGLE_SELECT_DROPDOWN":
                    return (
                      <Dropdown
                        key={filterOption.key}
                        overlay={
                          <DropdownMenu
                            options={filterOption.options}
                            onClick={({ key }) => {
                              setConversationDetailsObj({
                                ...conversationDetailsObj,
                                [filterOption.key]: key,
                              });
                            }}
                            selectionType="radio"
                            value={conversationDetailsObj[filterOption.key]}
                          />
                        }
                        disabled={isPublicView}
                      >
                        <FilterLabelButton
                          label={filterOption.label}
                          labelValue={
                            _.find(filterOption.options, {
                              key: conversationDetailsObj[filterOption.key],
                            })?.label
                          }
                          variant="plain"
                        />
                      </Dropdown>
                    );
                }
              })}
            </div>
          ) : null}
        </div>
        <div className={classes.bodyContainer} onScroll={onScrollBodyContainer}>
          {renderMessages()}
          <div
            ref={scrollEndRef}
            style={{ width: "100%", paddingBottom: 16 }}
          />
        </div>
        {!isPublicView ? (
          <div>
            {getIsAIResponseLoading(currentAIState) ? (
              <Button
                className={classes.stopGeneratingBtn}
                size={"medium"}
                variant={"outlined-subtle"}
                onClick={onClickStop}
                style={elevations["elevation-2B"]}
              >
                {t("common:stop_with_label", {
                  label: t("classRoom:generating"),
                })}
              </Button>
            ) : null}
            <div className={classes.conversationFooterContainer}>
              <Button
                size={"small"}
                variant={"progressive"}
                onClick={onSubmitMessages}
              >
                {"Submit"}
              </Button>
            </div>
          </div>
        ) : null}
      </div>
      {isSystemPromptModalOpen ? (
        <SystemPromptModal
          isPublicView={isPublicView}
          systemPrompt={systemPrompt}
          onModalClose={onSystemPromptModalClose}
          onSystemPromptUpdate={onSystemPromptUpdate}
        />
      ) : null}
      {isAttachmentsListOpen ? (
        <AttachmentsModal
          conversationAttachments={conversationAttachments}
          setIsAttachmentsListOpen={setIsAttachmentsListOpen}
          isPublicView={isPublicView}
          aiConversationId={aiConversationId}
          setIsLoading={setIsLoading}
        />
      ) : null}
      {isMemoriesModalOpen ? (
        <MemoriesModal onClose={onToggleMemoriesModal} />
      ) : null}
      {currentAIState === "LOADING" ? (
        <FullScreenLoader overlayContainer={classes.overlayContainer} />
      ) : null}
    </>
  );
};

const mapStateToProps = state => {
  return {
    userId: getUserId(state),
  };
};

const mapActionCreators = {
  onSendQuery,
  setToastMsg,
  resetAIConversation,
  updateAIConversation,
  createNewAiMessageInCache,
  createNewAIMessageMutationWithTemporaryId,
  createAIConversationAttachments,
  deleteAIConversationAttachment,
  onGenerateImageQuery,
  updateAIMessage,
};

export default compose(
  connect(mapStateToProps, mapActionCreators),
  graphql(getAIConversationsQueryWithPublicUrl, {
    name: "getAIConversations",
    skip: ({ aiConversationId }) => _.isEmpty(aiConversationId),
    options: ({ aiConversationId }) => ({
      fetchPolicy: "cache-and-network",
      variables: { id: aiConversationId },
    }),
    props({ getAIConversations, ownProps: { isData, isLoading } }) {
      const queryData = getAIConversationsQueryWithPublicUrlFromCache(
        getAIConversations.variables
      );

      const networkStatus = _.get(getAIConversations, "networkStatus");
      const messages = [..._.get(queryData, "messages.edges", [])].reverse();
      const metadata = _.get(queryData, "metadata", {}) || {};
      const publicUrl = _.get(queryData, "publicUrl", "");
      const conversationAttachments = _.get(queryData, "attachments", []);

      return {
        publicUrl,
        messages,
        conversationAttachments,
        metadata,
        isInitialState: _.isEmpty(messages),
        isData: !_.isEmpty(queryData),
        isLoading: _.includes([1, 2], networkStatus),
      };
    },
  }),
  withLoader
)(GPTConversation);
